import { BarChart3, Check, FileCheck2, KeyRound, RefreshCw, Rocket, ShieldCheck, Star, Upload } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { InlineNotice } from "../components/Feedback";
import type { AdminRatingSummary } from "../../domain/ratings";

interface ContentVersion {
  id: string;
  version: string;
  slug: string;
  status: string;
  manifestHash: string;
  createdAt: string;
  publishedAt?: string;
}

interface Report {
  valid: boolean;
  packageId?: string;
  version?: string;
  nodeCount: number;
  reachableNodeCount: number;
  terminalNodeCount: number;
  issues: Array<{ severity: "error" | "warning"; code: string; path: string; message: string }>;
}

export function AdminPage() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("admin-key") ?? "");
  const [keyDraft, setKeyDraft] = useState("");
  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [events, setEvents] = useState<Record<string, number>>({});
  const [ratings, setRatings] = useState<AdminRatingSummary[]>([]);
  const [packageText, setPackageText] = useState("");
  const [report, setReport] = useState<Report>();
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setError("");
    try {
      const [contentResult, analyticsResult, ratingsResult] = await Promise.all([
        api.admin<{ versions: ContentVersion[] }>("/content", adminKey),
        api.admin<{ events: Record<string, number> }>("/analytics", adminKey),
        api.admin<{ ratings: AdminRatingSummary[] }>("/ratings", adminKey)
      ]);
      setVersions(contentResult.versions);
      setEvents(analyticsResult.events);
      setRatings(ratingsResult.ratings);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos abrir el panel.");
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const unlock = (event: FormEvent) => {
    event.preventDefault();
    sessionStorage.setItem("admin-key", keyDraft);
    setAdminKey(keyDraft);
  };

  const parsePackage = () => {
    try {
      return JSON.parse(packageText);
    } catch {
      setError("El texto no es JSON válido. Corrige la sintaxis antes de validar.");
      return undefined;
    }
  };

  const validate = async () => {
    const parsed = parsePackage();
    if (!parsed) return;
    setPending(true);
    setError("");
    try {
      const result = await api.admin<{ report: Report }>("/content/validate", adminKey, {
        method: "POST",
        body: JSON.stringify({ package: parsed })
      });
      setReport(result.report);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos validar el paquete.");
    } finally {
      setPending(false);
    }
  };

  const importPackage = async () => {
    const parsed = parsePackage();
    if (!parsed) return;
    setPending(true);
    setError("");
    try {
      const result = await api.admin<{ report: Report }>("/content/import", adminKey, {
        method: "POST",
        body: JSON.stringify({ package: parsed })
      });
      setReport(result.report);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos importar el paquete.");
    } finally {
      setPending(false);
    }
  };

  const publish = async (item: ContentVersion) => {
    if (!window.confirm(`¿Publicar ${item.id}@${item.version}? Una versión publicada será inmutable.`)) return;
    setPending(true);
    try {
      await api.admin(`/content/${encodeURIComponent(item.id)}/${encodeURIComponent(item.version)}/publish`, adminKey, { method: "POST", body: "{}" });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos publicar la versión.");
    } finally {
      setPending(false);
    }
  };

  if (!adminKey) {
    return (
      <div className="admin-lock page-pad">
        <form onSubmit={unlock}>
          <KeyRound aria-hidden="true" />
          <h1>Panel editorial</h1>
          <p>La publicación, el contenido privado y la analítica requieren una llave separada de las salas.</p>
          <label htmlFor="admin-key">Llave editorial</label>
          <input id="admin-key" type="password" value={keyDraft} onChange={(event) => setKeyDraft(event.target.value)} autoComplete="current-password" required />
          {import.meta.env.DEV && <small>En desarrollo con <code>DEMO_MODE=true</code>, la llave inicial es <code>demo-admin</code>.</small>}
          <button className="button button-citron button-large" type="submit" disabled={!keyDraft}>Abrir panel</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-page page-pad">
      <header className="admin-heading">
        <div><h1>Publicación con puertas, no con sorpresas.</h1><p>Valida primero. Importa como borrador. Publica sólo una versión trazable.</p></div>
        <button className="button button-light" type="button" onClick={load}><RefreshCw aria-hidden="true" /> Actualizar</button>
      </header>
      {error && <InlineNotice tone="error">{error}</InlineNotice>}

      <section className="admin-overview">
        <div><ShieldCheck aria-hidden="true" /><span>Versiones</span><strong>{versions.length}</strong></div>
        <div><Rocket aria-hidden="true" /><span>Publicadas</span><strong>{versions.filter((item) => item.status === "published").length}</strong></div>
        <div><Check aria-hidden="true" /><span>Eventos recibidos</span><strong>{Object.values(events).reduce((sum, value) => sum + value, 0)}</strong></div>
        <div><Star aria-hidden="true" /><span>Valoraciones</span><strong>{ratings.reduce((sum, item) => sum + item.count, 0)}</strong></div>
      </section>

      <section className="admin-split">
        <div className="content-versions">
          <h2>Versiones de contenido</h2>
          <div className="version-table" role="table" aria-label="Versiones de contenido">
            {versions.map((item) => (
              <div className="version-row" role="row" key={`${item.id}@${item.version}`}>
                <div role="cell"><strong>{item.id}</strong><span>{item.version}</span></div>
                <span className={`status-tag status-${item.status}`} role="cell">{item.status}</span>
                <code role="cell">{item.manifestHash.slice(0, 10)}</code>
                <div role="cell">
                  {item.status === "draft" ? (
                    <button className="small-action" type="button" disabled={pending} onClick={() => publish(item)}><Rocket aria-hidden="true" /> Publicar</button>
                  ) : <span className="immutable-label"><ShieldCheck aria-hidden="true" /> Inmutable</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="analytics-list">
          <h2>Eventos minimizados</h2>
          {Object.keys(events).length ? Object.entries(events).map(([name, count]) => (
            <div key={name}><span>{name}</span><strong>{count}</strong></div>
          )) : <p className="empty-copy">Aún no hay eventos. Juega una sesión de prueba para verificar el flujo.</p>}
        </div>
      </section>

      <section className="admin-ratings">
        <div className="admin-section-heading"><div><h2>Valoración por experiencia</h2><p>Promedios verificados de salas terminadas. Las señales de mejora sólo aparecen aquí.</p></div><BarChart3 aria-hidden="true" /></div>
        <div className="ratings-admin-list">
          {ratings.map((item) => (
            <article className="rating-admin-row" key={item.experienceId}>
              <div className="rating-admin-title"><strong>{item.title}</strong><span>{item.count ? `${item.average?.toFixed(1)} de 5 · ${item.count} votos` : "Sin valoraciones"}</span></div>
              <div className="rating-distribution" aria-label={`Distribución de ${item.title}`}>
                {[5, 4, 3, 2, 1].map((value) => <span key={value}><b>{value}</b><i><em style={{ width: item.count ? `${(item.distribution[String(value) as keyof typeof item.distribution] / item.count) * 100}%` : "0%" }} /></i><small>{item.distribution[String(value) as keyof typeof item.distribution]}</small></span>)}
              </div>
              <div className="rating-admin-tags">
                {item.tags.length ? item.tags.slice(0, 6).map((tag) => <span className={tag.public ? "" : "is-friction"} key={tag.id}>{tag.label} · {tag.count}</span>) : <small>Aún no hay etiquetas.</small>}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="package-workbench">
        <div>
          <h2>Banco de validación</h2>
          <p>Pega un paquete JSON. El servidor verifica schema, referencias, variables, rutas terminales y markup inseguro.</p>
        </div>
        <label htmlFor="package-json">Paquete de experiencia</label>
        <textarea id="package-json" value={packageText} onChange={(event) => setPackageText(event.target.value)} rows={14} spellCheck={false} placeholder={'{\n  "schema_version": "1.0",\n  "experience": { ... }\n}'} />
        <div className="workbench-actions">
          <button className="button button-light" type="button" disabled={pending || !packageText} onClick={validate}><FileCheck2 aria-hidden="true" /> Validar sin guardar</button>
          <button className="button button-citron" type="button" disabled={pending || !packageText} onClick={importPackage}><Upload aria-hidden="true" /> Importar como borrador</button>
        </div>
        {report && (
          <div className={report.valid ? "validation-report is-valid" : "validation-report is-invalid"}>
            <div><strong>{report.valid ? "El paquete es estructuralmente válido" : "El paquete necesita correcciones"}</strong><span>{report.reachableNodeCount}/{report.nodeCount} nodos alcanzables · {report.terminalNodeCount} finales</span></div>
            {report.issues.map((issue, index) => <p key={`${issue.code}-${index}`}><code>{issue.severity} · {issue.code}</code><span>{issue.path}: {issue.message}</span></p>)}
          </div>
        )}
      </section>
    </div>
  );
}
